'use client';

import { Category, ContentType } from '@/graphql/graphql';
import { fetchCategoriesList } from '@/lib/api-categories';
import { CategoryDropdownMenu } from '@/types/categories';
import { useLocale, useTranslations } from 'next-intl';
import { useSelectedLayoutSegments } from 'next/navigation';
import { useEffect, useReducer, useRef, useState } from 'react';
import { CategoriesAction, CategoriesState, MatchMode } from './interfaces';

const categoriesInitialState: CategoriesState = {
    categoriesProgram: [],
    categoriesFormat: [],
    categoriesStudio: [],
    isLoaded: false,
};

/**
 * Each SET_CATEGORIES_* action replaces the corresponding array and marks the state as loaded.
 * RESET_FOR_LOCALE_CHANGE empties everything so the fetch effect re-triggers.
 */
const categoriesReducer = (state: CategoriesState, action: CategoriesAction): CategoriesState => {
    switch (action.type) {
        case 'SET_CATEGORIES_PROGRAM':
            return { ...state, categoriesProgram: action.payload };
        case 'SET_CATEGORIES_FORMAT':
            return { ...state, categoriesFormat: action.payload, isLoaded: true };
        case 'SET_CATEGORIES_STUDIO':
            return { ...state, categoriesStudio: action.payload };
        case 'RESET_FOR_LOCALE_CHANGE':
            return { ...categoriesInitialState };
        default:
            return state;
    }
};

const segmentsFromPath = (href: string): string[] =>
    href
        .split('/')
        .filter(Boolean)
        .map((s) => s.toLowerCase());

type UseControllerParams = {
    translateNavigation: (key: string) => string;
};

export const useController = ({ translateNavigation }: UseControllerParams) => {
    const locale = useLocale().toLowerCase();

    const [categoriesState, dispatch] = useReducer(categoriesReducer, categoriesInitialState);

    const [catMenuOpen, setCatMenuOpen] = useState<boolean>(false);

    const previousLocaleRef = useRef(locale);

    /** When the locale changes, reset the categories so the fetch effect re-runs */
    useEffect(() => {
        if (previousLocaleRef.current !== locale) {
            previousLocaleRef.current = locale;
            dispatch({ type: 'RESET_FOR_LOCALE_CHANGE' });
        }
    }, [locale]);

    const segments = useSelectedLayoutSegments();
    const currentSegments = Array.isArray(segments) ? segments.filter(Boolean).map((s) => s.toLowerCase()) : [];

    const isLinkActive = (href: string, matchMode: MatchMode = 'exact'): boolean => {
        const hrefSegments = segmentsFromPath(href ?? '/');

        if (matchMode === 'exact') {
            if (hrefSegments.length !== currentSegments.length) return false;
            return hrefSegments.every((segment, index) => segment === currentSegments[index]);
        }

        if (hrefSegments.length === 0 && currentSegments.length === 0) return true;
        if (hrefSegments.length === 0 || hrefSegments.length > currentSegments.length) return false;
        return hrefSegments.every((segment, index) => segment === currentSegments[index]);
    };

    /** Fetches program and format categories, then marks the state as loaded */
    useEffect(() => {
        if (categoriesState.isLoaded) return;

        const fetchCategories = async () => {
            const programCategoriesResponse = await fetchCategoriesList({ contentType: ContentType.Program });

            const hardcodedGenreIds: Record<string, string> = process.env.NEXT_PUBLIC_GENRE_CATALOG_MENU_IDS
                ? JSON.parse(process.env.NEXT_PUBLIC_GENRE_CATALOG_MENU_IDS)
                : {};

            const programCategories: CategoryDropdownMenu[] = [
                ...programCategoriesResponse.data,
                ...Object.keys(hardcodedGenreIds).map((key) => {
                    const genreKey = `genreCatalogMenuItems.${hardcodedGenreIds[key].toLowerCase()}.title`;
                    return {
                        id: key,
                        title: translateNavigation(genreKey),
                        isAnExcludedGenre: true,
                        catchPhrase: '',
                        image: '',
                    };
                }),
            ].sort((a, b) => (a.title || '').localeCompare(b.title || ''));

            dispatch({ type: 'SET_CATEGORIES_PROGRAM', payload: programCategories });

            const formatCategoriesResponse = await fetchCategoriesList({ contentType: ContentType.Format });

            const formatCategories = formatCategoriesResponse.data.sort((a: Category, b: Category) =>
                (a.title || '').localeCompare(b.title || ''),
            );

            dispatch({ type: 'SET_CATEGORIES_FORMAT', payload: formatCategories });
        };

        fetchCategories();
    }, [locale, categoriesState.isLoaded, translateNavigation]);

    /** Closes the category menu when clicking outside or on a link */
    const containerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!catMenuOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            const isLinkClick = event.target instanceof Element && event.target.matches('a');
            const isOutsideClick =
                containerRef.current && event.target instanceof Node && !containerRef.current.contains(event.target);

            if (isLinkClick || isOutsideClick) {
                setCatMenuOpen(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [catMenuOpen]);

    return {
        categoriesProgram: categoriesState.categoriesProgram,
        categoriesFormat: categoriesState.categoriesFormat,
        categoriesStudio: categoriesState.categoriesStudio,
        catMenuOpen,
        setCatMenuOpen,
        containerRef,
        isLinkActive,
    };
};
